import {
  useEffect,
  useLayoutEffect,
  useRef,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "../../lib/utils.js";
import { useTransitionPresence } from "../../hooks/useTransitionPresence.js";
import { MOTION_DURATION_MS } from "../../motion.js";
import { getLayerRoot } from "../../primitives/index.js";
import { useToastPresence } from "./toast-presence.js";

/* ---------------- Toast ----------------
   Notification system on our own overlay engine (increment 5.5 — locked
   decision #6: sonner was removed, toasts are built here).

   Imperative API: `toast("Saved", { tone: "healthy" })` from anywhere —
   a module-level store, no context needed. Mount ONE <Toaster /> in the
   shell to render the stack (bottom-right, z-toast, portaled into the
   layer root so toasts stay visible above modals and outside `inert`).

   AT: each toast IS its own live region — role="status" (polite), or
   role="alert" for critical — so announce() is deliberately NOT called
   (it would double-announce). Toasts are not Esc-dismissable and do not
   register in the layer stack (they never own focus or dismissal). */

export type ToastTone = "neutral" | "healthy" | "watch" | "critical" | "info";

export interface ToastOptions {
  title?: React.ReactNode;
  tone?: ToastTone;
  /** Replace an existing visible toast with the same key instead of stacking. */
  dedupeKey?: string;
  /** Auto-dismiss after ms (default 5000). `null` = sticky until closed. */
  durationMs?: number | null;
  /**
   * One follow-up the reader can take from the toast itself — "Undo" after a
   * removal is the canonical case. Selecting it runs the handler and dismisses
   * the toast, so a handler never fires twice from one toast.
   */
  action?: ToastAction;
}

export interface ToastAction {
  readonly label: string;
  readonly onClick: () => void;
}

export interface ToastEntry {
  id: number;
  dedupeKey?: string | undefined;
  message: React.ReactNode;
  title?: React.ReactNode;
  tone: ToastTone;
  durationMs: number | null;
  action?: ToastAction | undefined;
}

/* ── store ──────────────────────────────────────────────────────────────── */

let nextId = 1;
let entries: readonly ToastEntry[] = [];
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const getSnapshot = () => entries;

/** Show a toast. Returns its id (pass to dismissToast for manual control). */
export function toast(
  message: React.ReactNode,
  options: ToastOptions = {},
): number {
  const id = nextId++;
  const entry: ToastEntry = {
    id,
    dedupeKey: options.dedupeKey,
    message,
    title: options.title,
    tone: options.tone ?? "neutral",
    durationMs: options.durationMs === undefined ? 5000 : options.durationMs,
    action: options.action,
  };
  entries = options.dedupeKey
    ? [...entries.filter((e) => e.dedupeKey !== options.dedupeKey), entry]
    : [...entries, entry];
  notify();
  return id;
}

export function dismissToast(id: number): void {
  if (!entries.some((e) => e.id === id)) return;
  entries = entries.filter((e) => e.id !== id);
  notify();
}

/** Test hook — clears the store between specs. Not for runtime use. */
export function __resetToasts(): void {
  entries = [];
  nextId = 1;
  notify();
}

/* ── view ───────────────────────────────────────────────────────────────── */

const TONE_BAR: Record<ToastTone, string> = {
  neutral: "bg-pb-border-strong",
  healthy: "bg-pb-green",
  watch: "bg-pb-amber",
  critical: "bg-pb-red",
  info: "bg-pb-cyan",
};

interface ToastItemProps {
  entry: ToastEntry;
  /** False once the store dropped the entry; the item then plays its exit. */
  open: boolean;
  onExited: (id: number) => void;
}

function ToastItem({ entry, open, onExited }: ToastItemProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { present, phase } = useTransitionPresence(
    open,
    MOTION_DURATION_MS.state,
  );
  const transitioningOut = phase === "closing";

  useEffect(() => {
    if (entry.durationMs === null) return;
    const t = window.setTimeout(() => dismissToast(entry.id), entry.durationMs);
    return () => window.clearTimeout(t);
  }, [entry.id, entry.durationMs]);

  // The exiting toast stays mounted for its transition only. Take it out of
  // every interaction path before paint (React 18 serializes inert={false},
  // so toggle the attribute imperatively) and drop it once the exit is done.
  useLayoutEffect(() => {
    ref.current?.toggleAttribute("inert", transitioningOut);
  }, [transitioningOut]);
  useEffect(() => {
    if (!present) onExited(entry.id);
  }, [present, entry.id, onExited]);

  if (!present) return null;
  const atRest = phase === "open";

  return (
    <div
      ref={ref}
      // A departing toast is no longer a live region: nothing left to announce.
      role={
        transitioningOut
          ? undefined
          : entry.tone === "critical"
            ? "alert"
            : "status"
      }
      aria-hidden={transitioningOut ? "true" : undefined}
      data-motion-state={phase}
      className={cn(
        "pointer-events-auto relative overflow-hidden flex items-start gap-3 w-90 max-w-[92vw]",
        "rounded-lg border border-pb-border bg-pb-card-elevated shadow-lg pb-hairline ps-4 pe-2 py-3",
        // `translate-y-*` writes the `translate` property, so it is listed too.
        "transition-[opacity,transform,translate] will-change-[opacity,transform]",
        atRest
          ? "translate-y-0 opacity-100"
          : "translate-y-[var(--pbds-motion-distance-md)] opacity-0",
        transitioningOut
          ? "pointer-events-none duration-motion-state ease-motion-exit"
          : "duration-motion-overlay ease-motion-enter",
      )}
    >
      <span
        aria-hidden
        className={cn("absolute start-0 inset-y-0 w-1", TONE_BAR[entry.tone])}
      />
      <div className="min-w-0 flex-1">
        {entry.title && (
          <div className="text-sm font-medium text-pb-text leading-snug">
            {entry.title}
          </div>
        )}
        <div
          className={cn(
            "text-sm text-pb-text-secondary",
            entry.title && "mt-0.5",
          )}
        >
          {entry.message}
        </div>
      </div>
      {entry.action && (
        <button
          type="button"
          onClick={() => {
            entry.action?.onClick();
            dismissToast(entry.id);
          }}
          className="shrink-0 self-center h-7 px-2.5 rounded-md text-sm font-medium text-pb-text bg-pb-accent/15 hover:bg-pb-accent/25 transition-colors focus-ring"
        >
          {entry.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={() => dismissToast(entry.id)}
        aria-label="Dismiss notification"
        className="shrink-0 w-6 h-6 flex items-center justify-center rounded text-pb-text-muted hover:text-pb-text hover:pb-overlay-paint active:pb-overlay-paint-strong transition-colors focus-ring"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

/** Mount once (shell level). Renders the toast stack bottom-right. */
export function Toaster() {
  const entries = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const { items, onExited } = useToastPresence(entries);
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      role="region"
      aria-label="Notifications"
      className="fixed bottom-6 end-6 z-toast flex flex-col items-end gap-2 pointer-events-none"
    >
      {items.map(({ entry, open }) => (
        <ToastItem
          key={entry.id}
          entry={entry}
          open={open}
          onExited={onExited}
        />
      ))}
    </div>,
    getLayerRoot(),
  );
}
